import { useEffect, useState } from 'react';
import { doc, collection, limit, onSnapshot, query, updateDoc, where } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getAuth, signInWithCustomToken } from 'firebase/auth';
import { app, db } from './config';
import { FUNCTIONS_REGION } from './region';
import type { Gender } from './profile';

/**
 * How the reader is connected to this pupil.
 *
 *   owner    — created the account, consented for it, may mint a session for
 *              it and hand over the device.
 *   guardian — redeemed an invite the pupil's own side issued (م-3-01), and
 *              may READ. That is the whole of it.
 */
export type Relation = 'owner' | 'guardian';

export type Child = {
  uid: string;
  displayName: string;
  age: number | null;
  gender: Gender | null;
  country: string | null;
  grade: number | null;
  relation: Relation;
};

// Re-exported from a module with no Firebase import, so a screen's test can
// read the real number instead of restating it in a mock.
export { MIN_CHILD_AGE, MAX_CHILD_AGE } from '../auth/childAge';

/**
 * The children this parent owns.
 *
 * Queried by `managedBy` rather than kept as a list on the parent's own
 * document: the child's document is where parentage lives, it is what the
 * rules and the token callable both read, and a second copy on the parent
 * would be a second answer to the same question.
 */
/**
 * Everyone this reader may follow: the children they own, and the pupils who
 * invited them.
 *
 * Two queries because they are two different questions — Firestore has no OR
 * across fields — and one list because to a parent they are simply "my
 * children". The list is complete only when BOTH have answered: reporting
 * done after the first would show "no children yet" for as long as the other
 * takes, which a parent reads as their children having disappeared.
 */
export function useMyChildren(uid: string | null): { children: Child[]; loading: boolean } {
  const [owned, setOwned] = useState<Child[] | null>(null);
  const [followed, setFollowed] = useState<Child[] | null>(null);

  useEffect(() => {
    if (!uid) { setOwned([]); setFollowed([]); return undefined; }
    setOwned(null);
    setFollowed(null);

    const read = (relation: Relation) => (snap: { docs: { id: string; data: () => Record<string, unknown> }[] }) =>
      snap.docs.map((d) => {
        const data = d.data();
        return {
          uid: d.id,
          displayName: String(data.displayName ?? ''),
          age: typeof data.age === 'number' ? data.age : null,
          gender: data.gender === 'male' || data.gender === 'female' ? data.gender : null,
          country: typeof data.country === 'string' && data.country ? data.country : null,
          grade: Number.isInteger(data.grade) ? (data.grade as number) : null,
          relation,
        } as Child;
      });

    const stopOwned = onSnapshot(
      query(collection(db, 'users'), where('managedBy', '==', uid)),
      (snap) => setOwned(read('owner')(snap)),
      () => setOwned([]),
    );
    const stopFollowed = onSnapshot(
      query(collection(db, 'users'), where('guardians', 'array-contains', uid)),
      (snap) => setFollowed(read('guardian')(snap)),
      () => setFollowed([]),
    );
    return () => { stopOwned(); stopFollowed(); };
  }, [uid]);

  // Owning outranks following, so a pupil named by both appears once, with
  // the powers the stronger relation carries.
  const merged = new Map<string, Child>();
  for (const child of followed ?? []) merged.set(child.uid, child);
  for (const child of owned ?? []) merged.set(child.uid, child);

  return {
    children: [...merged.values()].sort((a, b) => a.displayName.localeCompare(b.displayName)),
    loading: owned === null || followed === null,
  };
}

const fns = () => getFunctions(app, FUNCTIONS_REGION);

export async function createChild(input: {
  displayName: string;
  age: number;
  /** Optional since App Review 5.1.1(v); see setChildGender. */
  gender?: Gender;
  country?: string;
  grade?: number;
}): Promise<string> {
  const call = httpsCallable<typeof input, { uid: string }>(fns(), 'createChild');
  const out = await call(input);
  return out.data.uid;
}

/**
 * A guardian recording their child's gender, after the fact.
 *
 * Adding a child no longer asks (App Review 5.1.1(v), 2026-09-19). A child
 * without a gender cannot join a live session — they are same-gender — and a
 * managed child may not edit their own profile, so the guardian is the one who
 * says, from the children list. validChildEdit is the rule that allows it.
 */
export async function setChildGender(childUid: string, gender: Gender): Promise<void> {
  if (gender !== 'male' && gender !== 'female') throw new Error('invalid-gender');
  await updateDoc(doc(db, 'users', childUid), { gender });
}

/**
 * Whether THIS session is a managed child, and whose.
 *
 * Read from the signed-in reader's own document, which they may always read.
 * It decides whether the app offers "hand the device to my child" or "give it
 * back to me".
 */
export function useManagedBy(uid: string | null): { managedBy: string | null; loading: boolean } {
  const [state, setState] = useState<{ managedBy: string | null; loading: boolean }>(
    { managedBy: null, loading: true }
  );
  useEffect(() => {
    if (!uid) { setState({ managedBy: null, loading: false }); return undefined; }
    setState({ managedBy: null, loading: true });
    return onSnapshot(
      doc(db, 'users', uid),
      (snap) => {
        const value = snap.data()?.managedBy;
        setState({ managedBy: typeof value === 'string' ? value : null, loading: false });
      },
      () => setState({ managedBy: null, loading: false })
    );
  }, [uid]);
  return state;
}

/** Whether this parent has a PIN — the only way back from a child session. */
export function useHasPin(uid: string | null): { hasPin: boolean; loading: boolean } {
  const [state, setState] = useState({ hasPin: false, loading: true });
  useEffect(() => {
    if (!uid) { setState({ hasPin: false, loading: false }); return undefined; }
    setState({ hasPin: false, loading: true });
    // The hash itself is readable by nobody; this flag lives on the parent's
    // own document so the app can tell whether the way back exists without
    // ever seeing the secret.
    return onSnapshot(
      doc(db, 'users', uid),
      (snap) => setState({ hasPin: snap.data()?.hasPin === true, loading: false }),
      () => setState({ hasPin: false, loading: false })
    );
  }, [uid]);
  return state;
}

/** One turn of recitation, as a parent reads it back. */
export type ChildRecitation = {
  id: string;
  from: { surah: number; ayah: number } | null;
  to: { surah: number; ayah: number } | null;
  errors: number;
  startedAt: number;
  endedAt: number;
};

/**
 * A child's recent turns, for their parent.
 *
 * Bounded, and sorted here rather than by the query. The rules check
 * parentage by READING the child's document, once per returned row, and
 * Firestore caps how many such reads one request may make — an unbounded
 * history would start failing at some size that depends on the cap rather
 * than on anything the reader did. The bound is also why no index is needed:
 * one equality filter, ordered in memory.
 */
export function useChildRecitations(
  childUid: string | null,
  max = 20
): { recitations: ChildRecitation[]; loading: boolean } {
  const [state, setState] = useState<{ recitations: ChildRecitation[]; loading: boolean }>(
    { recitations: [], loading: true }
  );
  useEffect(() => {
    if (!childUid) { setState({ recitations: [], loading: false }); return undefined; }
    setState({ recitations: [], loading: true });
    return onSnapshot(
      query(collection(db, 'recitations'), where('studentId', '==', childUid), limit(max)),
      (snap) => setState({
        recitations: snap.docs
          .map((d) => {
            const x = d.data();
            return {
              id: d.id,
              from: x.from ?? null,
              to: x.to ?? null,
              errors: Number(x.errors ?? 0),
              startedAt: Number(x.startedAt ?? 0),
              endedAt: Number(x.endedAt ?? 0),
            };
          })
          .sort((a, b) => b.endedAt - a.endedAt),
        loading: false,
      }),
      () => setState({ recitations: [], loading: false })
    );
  }, [childUid, max]);
  return state;
}

export async function setParentPin(pin: string): Promise<void> {
  const call = httpsCallable<{ pin: string }, { ok: boolean }>(fns(), 'setParentPin');
  await call({ pin });
}

/**
 * Hands the device to one of this parent's own children.
 *
 * Signing in as the child REPLACES this session, which is why the caller must
 * have a PIN first: without one there is no way back, and the parent would
 * have to spend one of their five daily verification codes to reach their own
 * account again.
 */
export async function switchToChild(childUid: string): Promise<void> {
  const call = httpsCallable<{ childUid: string }, { token: string }>(fns(), 'childToken');
  const out = await call({ childUid });
  await signInWithCustomToken(getAuth(app), out.data.token);
}

/**
 * Asks for a code that makes somebody a guardian of this pupil — م-3-01.
 *
 * Omit studentId to invite a guardian to follow YOU; pass one to invite a
 * second guardian for a child whose account you own. The code comes back
 * once and is never readable again: it rests as a digest.
 */
export async function createGuardianInvite(
  studentId?: string,
): Promise<{ code: string; expiresAt: number }> {
  const call = httpsCallable<{ studentId?: string }, { code: string; expiresAt: number }>(
    fns(), 'createGuardianInvite',
  );
  const out = await call(studentId ? { studentId } : {});
  return out.data;
}

/** Redeems such a code, making the caller a guardian who may read. */
export async function redeemGuardianInvite(code: string): Promise<{ studentId: string }> {
  const call = httpsCallable<{ code: string }, { studentId: string }>(
    fns(), 'redeemGuardianInvite',
  );
  const out = await call({ code });
  return out.data;
}

/** Back to the parent who owns this child session, if the PIN is right. */
export async function switchToParent(pin: string): Promise<void> {
  const call = httpsCallable<{ pin: string }, { token: string }>(fns(), 'parentToken');
  const out = await call({ pin });
  await signInWithCustomToken(getAuth(app), out.data.token);
}
